// trace-viewer/src/behaviors/pages.ts
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { TraceBehavior } from "./tracebehavior";
import type { PropertyValue } from "./tracebehavior";
import { Properties } from "./properties";
import { PagesData, PageData, PAGE_STATE_ENTER } from "../scenedata/behaviors/pagesdata";
import type { ValidationError } from "../scenedata/behaviordata";
import { collectScopedStateTargets } from "../core/canopy/stateScope";
import { isTimeTransition, type TimeTransition } from "../scenedata/behaviors/propertiesdata";
import { getTransitionManager } from "../core/TransitionManager";
import { interpolateValue } from "../core/canopy/TransitionUtils";
import { Logger } from "../utils/Logger";
import type { Playable } from "./playable";
import type { SceneObject } from "../core/SceneObject";

/**
 * Structural view of an AnimationSequence used by the `'exitFirst'` cascade.
 * Read via the string `getBehavior` lookup (a value import of
 * AnimationSequence would cycle through core/SceneObject).
 */
interface PageExitSequence {
    /** Awaited departure: exit slot delay + playback as one promise. */
    requestExit(): Promise<void>;
    /** Cancel a departure and resume steady state (navigate-back repair). */
    restoreSteadyState(): void;
}

/**
 * Pages behavior - Marks an object as a pages container
 *
 * When present, the object's children are treated as pages
 * (one active at a time, navigated via next/prev).
 * Supports autoplay with configurable duration and looping.
 *
 * When `pageSource` is `'firstChild'`, pages are the grandchildren —
 * children of the first child TransformNode (e.g. GLB root) — rather
 * than direct SceneObject children.
 *
 * Implements Playable so a parent Timeline can drive page switching.
 */
/**
 * Decks whose start page this session has already settled, keyed by the deck's
 * SceneObject and remembering WHICH pages were settled.
 *
 * `SceneObject.reload()` rebuilds a deck's behaviors while its SceneObject
 * children survive (SceneObject.ts "only SceneObject children survive
 * reload"), so `onAttach` runs a second time on a deck that has been paged
 * since it loaded. What is on screen then is this session's navigation, not a
 * file's view of itself, and the file rule must not be applied to it.
 *
 * Identity alone would be too coarse: `setSrc()`/`setData()` re-materialize the
 * same SceneObject from a DIFFERENT asset, and a deck arriving that way is new
 * content that has never been through the rule — skipping it would adopt
 * whatever page that file happens to show, the very thing this rule exists to
 * stop. So the mark is the page set's signature (node ids, in order): a reload
 * keeps the same nodes and is recognised, a swap brings new ones and is
 * enforced. Ids rather than the nodes themselves, so nothing here keeps a
 * destroyed node alive.
 */
const settledDecks = new WeakMap<SceneObject, string>();

/** A page assignment's resolved target. */
interface PageTarget {
    obj: SceneObject;
    props: Properties;
}

export class Pages extends TraceBehavior<PagesData> implements Playable {
    static readonly behaviorType = "Pages" as const;

    /** Cancels the pending autoplay advance (a `TransitionManager.after` timer, on the scene's clock). */
    private _autoplayTimer: (() => void) | null = null;
    private _playing = false;
    private _activeIndex = 0;
    /** Whether a page's state assignments have been applied since attach. */
    private _pageStatesApplied = false;
    /** Cancels the pending deactivation of the outgoing page (a `TransitionManager.after` timer). */
    private _deactivationTimer: (() => void) | null = null;

    /** Bumped on each animated page change so a stale exit-clip / timer
     *  continuation from a superseded change does not deactivate the current
     *  page — and so a superseded `'exitFirst'` cascade's deferred swap never
     *  runs. Scoped to deactivation and the pending swap — NOT the legacy
     *  _transitioning guard. */
    private _deactivationGeneration = 0;

    /** Deferred phase-2 swap of a sequenced page change
     *  (`transitionOrder: 'exitFirst'`): set while the outgoing exit cascade
     *  plays, consumed when the last departure settles. Any new page change
     *  supersedes it; the same target fast-forwards it; returning to the
     *  still-visible current page repairs the retained departures. */
    private _pendingSwap: { targetIndex: number; departures: PageExitSequence[] } | null = null;

    /** Cleanup for page-level property crossfade overlays. */
    private _pagePropertyCleanup: (() => void) | null = null;

    constructor(sceneObject: SceneObject, data: PagesData) {
        super(sceneObject, data);
        if (Pages._migrateLoopAlias(this.getAuthored())) {
            this._resolved = this._resolveAll();
        }
    }

    /**
     * `autoplayLoop` → `loop`, folded in at the data boundary rather than read
     * at each use: `getData()`, re-export, and the editor all see one field.
     *
     * The alias wins when present. No current writer emits it, so its presence
     * means a pre-rename writer just supplied it — on load it is the only
     * source, and on commit it is the fresh intent landing on top of an
     * already-migrated `loop`. Erasing it in the same pass keeps the ambiguity
     * from surviving into the next one.
     *
     * @returns whether anything changed (callers re-resolve).
     */
    private static _migrateLoopAlias(data: PagesData): boolean {
        const legacy = data as PagesData & { autoplayLoop?: boolean };
        if (legacy.autoplayLoop === undefined) return false;
        legacy.loop = legacy.autoplayLoop;
        delete legacy.autoplayLoop;
        return true;
    }

    /** Migration only — Pages has no validation rules of its own. */
    protected validate(data: PagesData): ValidationError[] {
        Pages._migrateLoopAlias(data);
        return [];
    }

    /**
     * Start at the first page, whatever the file shows (PAGES_SPEC §3: index 0,
     * non-first pages `active: false`).
     *
     * The editor pages by toggling the page objects' `active`, and a save used
     * to write that view into the file — a deck saved on page 2 arrived with
     * page 2 enabled, and a runtime starting at index 0 made the first tap a
     * visual no-op: 0→1 onto the page already showing (Trace#2064, #809). The
     * exporter now writes decks the way the spec reads them
     * (`normalizePageDecks`); this is the same rule for files written before
     * that, said once. Adopting the visible page instead would have made
     * "which page opens" depend on what the author last looked at — a second
     * contract. A deck with no pages yet (the editor adding the behavior to
     * an empty node) is left alone, and so is one whose pages carry a bound
     * `active` — see `_hasBoundActive`.
     *
     * This is the file's rule, so it runs once per deck per session: a
     * `reload()` reattaches behaviors over children that survived, and what
     * they show is navigation, not a file (see `settledDecks`). It does not
     * apply at all to `pageSource: 'firstChild'`, whose pages carry no
     * serialized visibility to have got wrong.
     */
    protected onAttach(_target: TransformNode): void {
        const nodes = this.getPageNodes();
        if (nodes.length === 0) return;

        if (this._resolved.pageSource === 'firstChild') {
            // These pages are a GLB's own nodes, and their visibility is not
            // serialized (PAGES_SPEC §3) — a fresh load has every one of them
            // enabled because that is Babylon's default, not because a file
            // says so. There is nothing to correct and no file to blame, so
            // the rule does not apply and the `setEnabled` paging path owns
            // the mode exactly as it did before.
            return;
        }

        const signature = nodes.map(n => n.uniqueId).join(',');
        if (settledDecks.get(this.sceneObject) === signature) {
            // A reattach (`reload()`), not a load: same deck, same pages. Keep
            // what is on screen — resetting it would throw away navigation the
            // session made and warn about a file that is not at fault — and
            // take the index from it, so the index and the screen still agree
            // and the next tap lands. Nothing enabled (or several) reads as
            // page 0.
            const shown = nodes.findIndex(n => n.isEnabled(false));
            this._activeIndex = shown < 0 ? 0 : shown;
            return;
        }
        settledDecks.set(this.sceneObject, signature);

        if (nodes.some((n, i) => n.isEnabled(false) !== (i === 0))) {
            // Same rule as the exporter (`normalizePageDecks`): a binding is
            // authored intent, not a view of it, so a deck with one is left as
            // written. Correcting it would be destructive rather than
            // cosmetic — `setActive` replaces the `$ref` with whatever boolean
            // it happened to hold at attach, and the binding is gone from the
            // file the next time it is saved. Checked here, inside the
            // deviation branch, because reading authored data is not free.
            if (nodes.some(n => this._hasBoundActive(n))) {
                this._activeIndex = 0;
                return;
            }
            Logger.warn(`[Pages] "${this.sceneObject.name}" was saved with a page other than the first visible — showing page 1; pages start at index 0`);
            nodes.forEach((n, i) => this._setPageActive(n, i === 0));
        }
        this._activeIndex = 0;
    }

    protected onPlayModeChanged(playing: boolean): void {
        if (playing) {
            // Play opens on the showing page's look, not on base: a deck whose
            // page 0 hides later steps must not open on the finished model.
            // Deferred like `event/onStart`: at load the targets attach after
            // this deck, and on a play toggle every Properties must first take
            // its play-mode rest pose and initialState. Test doubles without
            // the barrier apply inline.
            const traceScene = this.sceneObject.traceScene;
            const apply = () => this._applyShowingPageStates();
            if (traceScene?._deferStartEvent) traceScene._deferStartEvent(apply);
            else apply();
            // Only auto-cycle pages when autoplay is enabled.
            // The Playable interface (play/stop) is for Timeline-driven cycling
            // and should not be triggered by play mode alone.
            if (this._resolved.autoplay) {
                this.play();
            }
        } else {
            this.stop();
            // Stop snaps, never fades: kill any pageProps transition (one in
            // flight at Stop, or one just started by stop()'s page-0 reset)
            // and clear its overlays ourselves — the scene-level play-off
            // sweep cancels without callbacks, which would leave the incoming
            // page's Properties frozen mid-fade.
            if (this._pagePropertyCleanup) {
                getTransitionManager(this.sceneObject.babylonScene)
                    .cancel(`pageProps:${this.sceneObject.transform.uniqueId}`);
                this._pagePropertyCleanup();
                this._pagePropertyCleanup = null;
            }
        }
    }

    protected onDetach(): void {
        this._stopAutoplay();
        this._pendingSwap = null;
        this._pagePropertyCleanup?.();
        this._pagePropertyCleanup = null;
        this._cancelDeactivationTimer();
    }

    private _cancelDeactivationTimer(): void {
        this._deactivationTimer?.();
        this._deactivationTimer = null;
    }

    // ── Playable interface ──

    /**
     * Start or resume page cycling.
     * Both standalone autoplay and Timeline use the same internal timer —
     * Pages always drives its own page switching at pageDuration rate.
     */
    play(): void {
        this._playing = true;
        // Ensure at least one page is active (resume or start from _activeIndex, not 0)
        const nodes = this.getPageNodes();
        if (nodes.length > 0 && !nodes.some(n => n.isEnabled(false))) {
            this.showPage(this._activeIndex);
        }
        this._startAutoplay();
    }

    /** Pause page cycling, freezing on the current page. */
    pause(): void {
        this._playing = false;
        this._stopAutoplay();
    }

    /** Stop page cycling and reset to page 0. */
    stop(): void {
        this._playing = false;
        this._stopAutoplay();
        this.showPage(0);
    }

    /**
     * Seek to a specific time in seconds.
     * Jumps to the page corresponding to the given time.
     * Does not affect play/pause state — if playing, the autoplay
     * timer restarts from the new position.
     */
    seek(time: number): void {
        const pageCount = this.getPageNodes().length;
        if (pageCount === 0) return;

        const pageDuration = this._resolved.pageDuration ?? 5;
        const index = Math.min(
            Math.floor(time / pageDuration),
            pageCount - 1,
        );

        this.showPage(index);

        // Restart the timer from the new position if playing
        if (this._playing) {
            this._startAutoplay();
        }
    }

    /** Total duration across all pages (for Timeline). */
    getTotalDuration(): number {
        const nodes = this.getPageNodes();
        const pageDuration = this._resolved.pageDuration ?? 5;
        return nodes.length * pageDuration;
    }

    // ── Public API ──

    /**
     * Advance to the next page.
     *
     * Returns a resolved Promise once the transition is *initiated* (the states
     * engine is fire-and-forget — it drives the transition on its own timer);
     * `Promise<void>` is retained for API stability with existing awaiters.
     * @param wrap — wrap around from last to first (default true)
     * @param animate — play the page transition (default true)
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    async next(wrap = true, animate = true): Promise<void> {
        const nodes = this.getPageNodes();
        if (nodes.length === 0) return;
        const newIndex = this._cycleIndex(this._activeIndex, 1, nodes.length, wrap);
        if (newIndex !== this._activeIndex) {
            this.showPage(newIndex, animate);
        }
    }

    /**
     * Go to the previous page.
     *
     * See {@link next} for the return-value contract.
     * @param wrap — wrap around from first to last (default true)
     * @param animate — play the page transition (default true)
     */
    // eslint-disable-next-line @typescript-eslint/require-await
    async prev(wrap = true, animate = true): Promise<void> {
        const nodes = this.getPageNodes();
        if (nodes.length === 0) return;
        const newIndex = this._cycleIndex(this._activeIndex, -1, nodes.length, wrap);
        if (newIndex !== this._activeIndex) {
            this.showPage(newIndex, animate);
        }
    }

    /** Get the total number of pages. */
    getPageCount(): number {
        return this.getPageNodes().length;
    }

    /**
     * Get the page TransformNodes for this container.
     * - `pageSource: 'self'` (default): the transform of each child SceneObject,
     *   excluding resolved `shared` children (persistent, non-page content).
     *   An unresolvable shared path excludes nothing — the child stays a page.
     * - `pageSource: 'firstChild'`: children of the first child TransformNode
     *   (GLB root). `shared` is ignored in this mode.
     */
    getPageNodes(): TransformNode[] {
        if (this._resolved.pageSource === 'firstChild') {
            return this._getNestedChildren();
        }
        const children = this.sceneObject.getChildren();
        const shared = this._resolved.shared;
        if (!shared || shared.length === 0) {
            return children.map(c => c.transform);
        }
        const excluded = new Set<SceneObject>();
        for (const path of shared) {
            const obj = this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject);
            if (obj) excluded.add(obj);
        }
        return children.filter(c => !excluded.has(c)).map(c => c.transform);
    }

    /**
     * Get the index of the currently active page.
     * Preserved across showAllPages() so it can be restored.
     */
    getActivePageIndex(): number {
        return this._activeIndex;
    }

    /** Show all pages simultaneously (overview mode, used by editor). */
    showAllPages(): void {
        for (const node of this.getPageNodes()) {
            this._setPageActive(node, true);
        }
    }

    /** True when more than one page is currently enabled (show-all overview). */
    isShowingAll(): boolean {
        const nodes = this.getPageNodes();
        if (nodes.length < 2) return false;
        let count = 0;
        for (const n of nodes) {
            if (n.isEnabled(false)) {
                count++;
                if (count > 1) return true;
            }
        }
        return false;
    }

    // ── Internals ──

    /** Compute the next index when cycling with optional wrapping. */
    private _cycleIndex(current: number, direction: number, count: number, wrap: boolean): number {
        let next = current + direction;
        if (wrap) {
            if (next < 0) next = count - 1;
            else if (next >= count) next = 0;
        } else {
            next = Math.max(0, Math.min(next, count - 1));
        }
        return next;
    }

    /**
     * In `pageSource: 'firstChild'` mode, get the children of the first child
     * TransformNode.
     */
    private _getNestedChildren(): TransformNode[] {
        const childTransforms = this.sceneObject.getChildTransforms();
        if (childTransforms.length === 0) return [];
        return childTransforms[0].getChildren<TransformNode>();
    }

    /**
     * Show a specific page by index, hiding all others.
     *
     * The states engine (`_showPageWithStates`) is the *only* transition engine
     * (PAGES_SPEC.md §4, P-11). It handles both authored transitions (page state assignments,
     * page-property crossfade, `_pageEnter`) and the plain no-transition case
     * (empty map → activate target, deactivate the rest). The legacy
     * AnimationSequence exit/enter path was removed: page changes no longer
     * auto-trigger `getExit().play()` / `playSequence()` over the page subtree.
     * AnimationSequence remains a first-class clip player that states can drive
     * via the `kind:'animation-clip'` binding (PAGES_SPEC.md §17).
     *
     * Concurrency is managed by the states engine's deactivation-timer
     * replacement (each call cancels and re-arms the timer); there is no
     * `_transitioning` guard.
     */
    showPage(index: number, animate = false): void {
        const nodes = this.getPageNodes();
        if (nodes.length === 0) return;
        index = Math.max(0, Math.min(index, nodes.length - 1));

        // Reset the autoplay timer on every page change (manual or programmatic)
        // when actively playing. This prevents stale timers from firing mid-transition
        // or immediately after a manual navigation, and ensures the full page duration
        // is measured from the moment the new page becomes active.
        // Gated on _playing (not autoplay data) so timeline scrubbing in paused/edit
        // mode doesn't start the timer.
        if (this._playing) {
            this._stopAutoplay();
            this._scheduleNext((this._resolved.pageDuration ?? 5) * 1000);
        }

        // ── Sequenced page change, fast-forward rule (`transitionOrder: 'exitFirst'`) ──
        // A pending phase-2 swap is superseded by ANY new page change
        // (last-intent-wins). Re-requesting the SAME target — an impatient
        // tap, or the autoplay tick when a cascade outlives the dwell (the
        // starvation guard) — fast-forwards: the exits already played, so
        // complete the deferred swap now without restarting the cascade.
        // Returning to the still-visible current page repairs the retained
        // departures (the page never deactivated, so no lifecycle event will);
        // any other target just drops the pending swap and re-evaluates below
        // with the old page still the visible one.
        if (this._pendingSwap !== null) {
            const { targetIndex, departures } = this._pendingSwap;
            this._pendingSwap = null;
            if (targetIndex === index) {
                this._showPageWithStates(index, nodes, this._resolved.pages ?? [], animate, true);
                return;
            }
            // Animate-gated like the cascade itself: an INSTANT return is
            // edit-mode territory (e.g. leaving play mode routes stop() →
            // showPage(0)) — the behaviors' own stop() handles that teardown.
            if (animate && index === this._activeIndex) {
                for (const seq of departures) {
                    try { seq.restoreSteadyState(); } catch (e) {
                        Logger.warn(`[Pages] departure repair failed: ${String(e)}`);
                    }
                }
            }
        }

        // ── Sequenced page change, phase 1 (`transitionOrder: 'exitFirst'`) ──
        // An animated change first plays the outgoing page's exit cascade to
        // completion — the incoming page is NOT activated during this phase.
        // Each departure is an AnimationSequence.requestExit(): the behavior
        // owns its slot delay and resolves (without playing) if superseded,
        // so the barrier can never hang. Phase 2 (the actual swap: page
        // states, activation — which fires the incoming page's activation
        // lifecycle) runs when the last departure settles, exactly as if the
        // change had begun at that moment; the dwell clock is re-anchored
        // then, so a long cascade never starves the incoming page's dwell.
        // With no exit clips anywhere on the outgoing page the swap below
        // runs immediately — byte-for-byte the 'concurrent' behavior.
        if (animate && this._resolved.transitionOrder === 'exitFirst' && index !== this._activeIndex) {
            const oldIndex = this._activeIndex;
            const outObj = oldIndex >= 0 && oldIndex < nodes.length
                ? this.sceneObject.traceScene.findSceneObjectByTransform(nodes[oldIndex])
                : undefined;
            const departures = outObj ? this._collectExitCascade(outObj) : [];
            if (departures.length > 0) {
                // The bumped generation retires a previous change's in-flight
                // settles and deactivation continuations.
                const gen = ++this._deactivationGeneration;
                this._pendingSwap = { targetIndex: index, departures };
                void Promise.all(departures.map(d => d.requestExit())).then(() => {
                    if (this._pendingSwap === null || gen !== this._deactivationGeneration) return;
                    this._pendingSwap = null;
                    // Re-anchor the dwell clock at the moment the incoming
                    // page actually appears.
                    if (this._playing) {
                        this._stopAutoplay();
                        this._scheduleNext((this._resolved.pageDuration ?? 5) * 1000);
                    }
                    this._showPageWithStates(index, nodes, this._resolved.pages ?? [], animate, true);
                });
                return;
            }
        }

        this._showPageWithStates(index, nodes, this._resolved.pages ?? [], animate);
    }

    /** An `advanceOnTap` deck listens to taps on its whole content (pages
     *  and shared children alike) — this registration also makes the
     *  pickability sweep light the deck's content up. Reads resolved data
     *  directly: it runs per tap and per sweep (never getData(), which
     *  deep-clones). */
    override wantsDescendantPointerEvents(): boolean {
        return this._resolved.advanceOnTap === true;
    }

    /**
     * An unclaimed tap anywhere on the deck's content advances one page
     * (nearest deck wins — the bubbling walk stops at the first claim).
     * Wrap follows `loop` (off = hold on the last page, on = wrap);
     * the change is animated. `showPage` restarts the autoplay timer while
     * playing, so a tap never double-advances — and a tap mid-cascade
     * fast-forwards the pending swap via the same-target rule above.
     */
    override onDescendantPointerEvent(type: 'select' | 'hover' | 'active', isEnter: boolean, _target: SceneObject): boolean {
        if (type !== 'select' || !isEnter || this._resolved.advanceOnTap !== true) return false;
        void this.next(this._resolved.loop ?? false, true);
        return true;
    }

    /**
     * Collect the `'exitFirst'` departure cascade across an outgoing page
     * root and its SceneObject descendants: every object whose
     * AnimationSequence has a non-empty exit slot. Mirrors the state-scope
     * walk's pruning at nested boundaries: a page that is ITSELF a deck (or
     * a nested deck inside the page) departs as one unit via its own clip —
     * an inner deck's (hidden) pages must never gate the outer flip.
     */
    private _collectExitCascade(pageObj: SceneObject): PageExitSequence[] {
        const out: PageExitSequence[] = [];
        const walk = (obj: SceneObject): void => {
            const seq = this._getExitSequence(obj);
            if (seq) out.push(seq);
            // A deck departs as one unit via its own clip — whether it IS the
            // page (deck-as-page) or sits inside it. Never descend into it.
            if (obj.getBehavior(Pages) !== null) return;
            for (const child of obj.getChildren()) {
                walk(child);
            }
        };
        walk(pageObj);
        return out;
    }

    // ── State-based transitions ──

    /**
     * Show a page using state-based transitions.
     * For each assignment in pages[index].states, calls setState() on the
     * target's Properties behavior. Targets assigned on other pages but not
     * on this one revert to base state.
     * Activates/deactivates page nodes as usual.
     */
    private _showPageWithStates(
        newIndex: number,
        nodes: TransformNode[],
        pages: PageData[],
        animate: boolean,
        skipExitPhase = false,
    ): void {
        const oldIndex = this._activeIndex;

        const stateMap = this._pageStateMap(pages, newIndex);

        // Build a fallback transition from the Pages-level default.
        // Used when a child's state definition has no transition of its own.
        // Normalized per-field (not `??` on the whole object): createDefaultData
        // recurses into the optional `transition` struct and can emit it with
        // `easing`/`delay` but no `duration` — a whole-object fallback would
        // accept that and produce NaN timers.
        const authoredT = this._resolved.transition;
        const fallbackTransition: TimeTransition = {
            duration: authoredT?.duration ?? 0.5,
            easing: authoredT?.easing ?? 'easeInOut',
            ...(authoredT?.delay !== undefined ? { delay: authoredT.delay } : {}),
        };

        // Every target assigned on ANY page: those not assigned on the new
        // page revert to base.
        const allMappedPaths = this._allMappedPaths(pages);

        this._activeIndex = newIndex;
        this._pageStatesApplied = true;
        const instant = !animate;

        // Cancel any pending deactivation from a previous transition and bump the
        // deactivation generation so a stale exit-clip / timer continuation from a
        // superseded change can no longer deactivate the (now current) page.
        this._cancelDeactivationTimer();
        const deactivationGen = ++this._deactivationGeneration;

        if (animate) {
            // Deactivate all pages except outgoing and incoming.
            // This cleans up pages left active by a previous interrupted transition.
            for (let i = 0; i < nodes.length; i++) {
                if (i !== newIndex && i !== oldIndex) {
                    this._setPageActive(nodes[i], false);
                }
            }
            // Ensure outgoing and incoming are active
            this._setPageActive(nodes[newIndex], true);
            if (oldIndex !== newIndex && oldIndex >= 0 && oldIndex < nodes.length) {
                this._setPageActive(nodes[oldIndex], true);
            }
        }

        // Resolve all mapped targets once (avoids N+1 resolveSceneObject calls)
        const resolvedTargets = this._resolveTargets(allMappedPaths);

        let maxDuration = 0;
        // Every member the new page just assigned, directly or through another
        // assignment's fan. The revert loop below must leave these alone.
        const assignedMembers = new Set<SceneObject>();
        for (const { member, props, state: stateName } of this._fanAssignments(stateMap, resolvedTargets)) {
            assignedMembers.add(member);
            // Mirror the resolution order in _recomputeFromStack:
            // incoming state's transition > previous state's transition >
            // fallback. Every fanned member contributes to maxDuration — a
            // member's own state may declare a longer transition than the
            // root's, and the deactivation barrier must outlast all of them.
            const stateT = props.getStateTransition(stateName);
            const prevState = props.getActiveState();
            const prevT = prevState ? props.getStateTransition(prevState) : undefined;
            const effectiveT = stateT ?? prevT ?? fallbackTransition;
            if (animate && isTimeTransition(effectiveT)) {
                maxDuration = Math.max(maxDuration, effectiveT.duration + (effectiveT.delay ?? 0));
            }
            props.setState(stateName, instant, fallbackTransition);
        }

        // Revert objects not assigned on the new page (entering their home page).
        for (const props of this._fanReverts(stateMap, resolvedTargets, assignedMembers)) {
            if (animate) {
                // Snap to the enter pose (`_pageEnter`) before animating back to base.
                const snapped = this._snapEnterState(props);
                // Timer uses the snap state's transition (that's what drives the
                // animation), falling back to the page-level default.
                const enterTransition = (snapped ? props.getStateTransition(snapped) : undefined) ?? fallbackTransition;
                if (isTimeTransition(enterTransition)) maxDuration = Math.max(maxDuration, enterTransition.duration + (enterTransition.delay ?? 0));
            }
            props.setState(undefined, instant, fallbackTransition);
        }

        // ── Resolve per-page transition timing ──
        // The incoming page's _pageEnter transition overrides the Pages-level
        // fallback, allowing per-page timing (e.g., slow entry on page 2,
        // fast on page 3). Used by the page-property crossfade.
        let pageTransition = fallbackTransition;
        if (animate && oldIndex !== newIndex) {
            const newNode = nodes[newIndex];
            const newObj = newNode ? this.sceneObject.traceScene.findSceneObjectByTransform(newNode) : undefined;
            const newPageProps = newObj?.getBehavior(Properties);
            if (newPageProps) {
                const enterT = newPageProps.getStateTransition(PAGE_STATE_ENTER);
                if (enterT && isTimeTransition(enterT)) {
                    pageTransition = enterT;
                }
            }
        }

        // ── Page property crossfade ──
        // When both old and new page objects have Properties with matching
        // property names, crossfade them on the incoming page so children
        // inheriting those properties see smooth interpolation.
        if (animate && oldIndex !== newIndex) {
            const crossfadeDuration = this._processPagePropertyCrossfade(
                oldIndex, newIndex, nodes, pageTransition,
            );
            maxDuration = Math.max(maxDuration, crossfadeDuration);
        }

        // ── Outgoing page visibility ──
        // The old page needs to stay visible during the transition ONLY when
        // something on it is animating out: an assignment on the NEW page that
        // targets an old-page object (the `_pageExit` pattern), or an exit clip
        // (checked below). Reverts never need it — a reverting object's home
        // page is the one being ENTERED, not left. Everything else hides
        // immediately, so a plain animated switch never shows two pages at once.
        let outgoingAnimating = false;
        const oldNodeValid = oldIndex !== newIndex && oldIndex >= 0 && oldIndex < nodes.length;
        if (animate && oldNodeValid) {
            const oldNode = nodes[oldIndex];
            for (const path of stateMap.keys()) {
                const t = resolvedTargets.get(path)?.obj.transform;
                if (t && (t === oldNode || t.isDescendantOf(oldNode))) {
                    outgoingAnimating = true;
                    break;
                }
            }
        }

        // ── Outgoing page exit clip (P-18 awaited exit phase) ──
        // If the outgoing page root has a bound AnimationSequence exit clip,
        // play it and gate deactivation on its completion as well. This is the
        // "await exit before the page flips" capability the legacy engine
        // uniquely provided (PAGES_SPEC.md §17.4), now delegated to the shared
        // state layer's awaited-exit primitive instead of a Pages-local walk.
        //
        // Deactivation is gated on ALL pending signals — the page-states/crossfade
        // timer AND the exit clip — via a small pending-count barrier. Only the
        // LAST to finish performs the deactivation, so a short page-states
        // transition can never cut a longer exit clip (or vice versa). The
        // generation guard prevents a superseded change from firing at all.
        let pending = 0;
        const settleOne = (): void => {
            if (deactivationGen !== this._deactivationGeneration) return;
            if (--pending > 0) return;
            this._cancelDeactivationTimer();
            for (let i = 0; i < nodes.length; i++) {
                this._setPageActive(nodes[i], i === this._activeIndex);
            }
        };

        const timerPending = animate && maxDuration > 0;
        let exitClipPending = false;
        // An 'exitFirst' phase-2 call passes `skipExitPhase`: the root's exit
        // already played in the phase-1 cascade and must not replay here.
        if (animate && oldNodeValid && !skipExitPhase) {
            const outObj = this.sceneObject.traceScene.findSceneObjectByTransform(nodes[oldIndex]);
            const outExit = outObj ? this._playPageExitClip(outObj) : undefined;
            if (outExit) {
                exitClipPending = true;
                pending++;
                void outExit.then(settleOne);
            }
        }

        // Nothing on the outgoing page animates out — hide it now instead of
        // letting it linger fully visible for maxDuration.
        if (animate && oldNodeValid && !outgoingAnimating && !exitClipPending) {
            this._setPageActive(nodes[oldIndex], false);
        }

        if (timerPending) {
            pending++;
            this._deactivationTimer = getTransitionManager(this.scene).after(maxDuration * 1000, () => {
                this._deactivationTimer = null;
                settleOne();
            });
        }

        if (!timerPending && !exitClipPending) {
            // Nothing to wait for — deactivate immediately.
            for (let i = 0; i < nodes.length; i++) {
                this._setPageActive(nodes[i], i === newIndex);
            }
        }
    }

    /**
     * Re-apply the showing page's state assignments, instantly: the same
     * targets, scoped fan-out and states as showing the page, without touching
     * the page nodes, transitions or timers. For an editor whose own states
     * reset dropped the page's look. With `scope`, only assignments whose
     * target is in it are re-applied. A deck showing all pages, or one whose
     * page states were never applied (no page shown since attach), poses
     * nothing: there is no page look on screen to give back.
     */
    reapplyPageStates(options: { scope?: ReadonlySet<SceneObject> } = {}): void {
        if (!this._pageStatesApplied || this.isShowingAll()) return;
        const stateMap = this._pageStateMap(this._resolved.pages ?? [], this._activeIndex);
        const targets = this._resolveTargets(stateMap.keys());
        if (options.scope) {
            for (const [path, target] of targets) {
                if (!options.scope.has(target.obj)) targets.delete(path);
            }
        }
        for (const { props, state } of this._fanAssignments(stateMap, targets)) {
            props.setState(state, true);
        }
    }

    /**
     * Pose the showing page's look instantly — its assignments set, every other
     * mapped target at base — without touching page nodes, transitions or
     * timers. What entering play mode does; a deck detached, out of play or
     * showing all pages by then poses nothing.
     */
    private _applyShowingPageStates(): void {
        if (!this.attachedNode || !this.playMode || this.isShowingAll()) return;
        const pages = this._resolved.pages ?? [];
        const stateMap = this._pageStateMap(pages, this._activeIndex);
        const targets = this._resolveTargets(this._allMappedPaths(pages));
        const assigned = new Set<SceneObject>();
        for (const { member, props, state } of this._fanAssignments(stateMap, targets)) {
            assigned.add(member);
            props.setState(state, true);
        }
        for (const props of this._fanReverts(stateMap, targets, assigned)) {
            props.setState(undefined, true);
        }
        this._pageStatesApplied = true;
    }

    /** Every target any page assigns. */
    private _allMappedPaths(pages: PageData[]): Set<string> {
        const paths = new Set<string>();
        for (const page of pages) {
            for (const a of page?.states ?? []) paths.add(a.target);
        }
        return paths;
    }

    /**
     * The members to revert to base: every mapped target the page does not
     * assign, fanned through the same scope walk (Base participation = the
     * member declares at least one state). A `$self` or group target's Base
     * fan reaches every descendant that declares a state, including members
     * the page assigned (`assigned`); those keep their assignment.
     */
    private *_fanReverts(
        stateMap: Map<string, string>,
        targets: Map<string, PageTarget>,
        assigned: ReadonlySet<SceneObject>,
    ): Generator<Properties> {
        for (const [path, target] of targets) {
            if (stateMap.has(path)) continue;
            for (const member of collectScopedStateTargets(target.obj, undefined)) {
                if (assigned.has(member)) continue;
                const props = member === target.obj ? target.props : member.getBehavior(Properties);
                if (props) yield props;
            }
        }
    }

    /**
     * Page `index`'s assignments as target → state. Later duplicates of the
     * same target override earlier ones (documented in pagesdata.ts).
     */
    private _pageStateMap(pages: PageData[], index: number): Map<string, string> {
        const stateMap = new Map<string, string>();
        for (const a of pages[index]?.states ?? []) {
            stateMap.set(a.target, a.state);
        }
        return stateMap;
    }

    /** Resolve target paths relative to the deck; a target without Properties is skipped. */
    private _resolveTargets(paths: Iterable<string>): Map<string, PageTarget> {
        const targets = new Map<string, PageTarget>();
        for (const path of paths) {
            const obj = this.sceneObject.traceScene.resolveSceneObject(path, this.sceneObject);
            const props = obj?.getBehavior(Properties);
            if (obj && props) targets.set(path, { obj, props });
        }
        return targets;
    }

    /**
     * Each member a page's assignments reach, with its state. setState is a
     * SCOPED runtime op (STATE_SCOPE_SPEC): each assignment fans from its target
     * to every participating descendant in the target's scope, pruned at
     * stateGroup boundaries — the same walk trace/setState uses. A leaf target
     * fans to nothing (the collect returns just the target).
     */
    private *_fanAssignments(
        stateMap: Map<string, string>,
        targets: Map<string, PageTarget>,
    ): Generator<{ member: SceneObject; props: Properties; state: string }> {
        for (const [path, state] of stateMap) {
            const target = targets.get(path);
            if (!target) continue;
            for (const member of collectScopedStateTargets(target.obj, state)) {
                const props = member === target.obj ? target.props : member.getBehavior(Properties);
                if (props) yield { member, props, state };
            }
        }
    }

    /**
     * Enter snap for objects reverting to their home page: snap to `_pageEnter`
     * (instant) before animating back to base, so arrivals animate in from the
     * authored enter pose. Returns the snapped state name, or undefined if the
     * object has no `_pageEnter` state.
     */
    private _snapEnterState(props: Properties): string | undefined {
        if (props.hasState(PAGE_STATE_ENTER)) {
            // Intentionally instant — snap to the enter pose before animating.
            props.setState(PAGE_STATE_ENTER, true);
            return PAGE_STATE_ENTER;
        }
        return undefined;
    }

    /**
     * Play a bound AnimationSequence exit clip on a page root, if present, and
     * return its completion Promise (P-18 consumption on the 'concurrent'
     * path — root only; subtree exits are 'exitFirst' territory, per P-15's
     * removal of un-opted subtree auto-triggering). Routed through
     * `requestExit` so the exit slot's authored `delay` is honored. Uses the
     * string `getBehavior` lookup to avoid a value import of
     * AnimationSequence (a cycle through core/SceneObject).
     */
    private _playPageExitClip(pageObj: SceneObject): Promise<void> | undefined {
        return this._getExitSequence(pageObj)?.requestExit();
    }

    /**
     * THE structural probe for an exit-capable AnimationSequence on an
     * object — shared by the `'exitFirst'` cascade collection and the
     * `'concurrent'` root-exit gate, so the duck-typed contract (string
     * `getBehavior` to avoid a value-import cycle through core/SceneObject)
     * is encoded exactly once. Returns null when the behavior is absent,
     * predates `requestExit`, or has no authored exit clip.
     */
    private _getExitSequence(obj: SceneObject): PageExitSequence | null {
        const animSeq = obj.getBehavior('AnimationSequence') as unknown as (PageExitSequence & {
            getExit?: () => { isEmpty: boolean } | undefined;
        }) | null;
        if (!animSeq || typeof animSeq.requestExit !== 'function') return null;
        if (animSeq.getExit?.()?.isEmpty !== false) return null;
        return animSeq;
    }

    /**
     * Crossfade properties between outgoing and incoming page objects.
     *
     * For each property declared on the incoming page that also exists on
     * the outgoing page, overlay the outgoing value on the incoming page
     * and animate to the incoming page's authored value. Children inheriting
     * these properties see smooth interpolation via walk-on-read.
     *
     * @returns Max transition duration in seconds.
     */
    private _processPagePropertyCrossfade(
        oldIndex: number,
        newIndex: number,
        nodes: TransformNode[],
        fallbackTransition: TimeTransition,
    ): number {
        const traceScene = this.sceneObject.traceScene;

        const oldNode = nodes[oldIndex];
        const newNode = nodes[newIndex];
        if (!oldNode || !newNode) return 0;

        const oldObj = traceScene.findSceneObjectByTransform(oldNode);
        const newObj = traceScene.findSceneObjectByTransform(newNode);
        if (!oldObj || !newObj) return 0;

        const oldProps = oldObj.getBehavior(Properties);
        const newProps = newObj.getBehavior(Properties);
        if (!oldProps || !newProps) return 0;

        // Find properties declared on the incoming page that also resolve on the outgoing page.
        const candidates = new Map<string, { from: PropertyValue; to: PropertyValue }>();
        for (const name of newProps.getPropertyNames()) {
            const fromVal = oldProps.getValue(name);
            const toVal = newProps.getValue(name);
            if (fromVal !== undefined && toVal !== undefined && fromVal !== toVal) {
                candidates.set(name, { from: fromVal, to: toVal });
            }
        }

        if (candidates.size === 0) return 0;

        // Clean up any in-flight page property crossfade from a previous transition.
        this._pagePropertyCleanup?.();

        // Snap incoming page properties to outgoing values.
        const propNames = [...candidates.keys()];
        for (const [name, { from }] of candidates) {
            newProps._setTransitionValue(name, from);
        }

        // Register cleanup so interrupted transitions don't leave stale overlays.
        this._pagePropertyCleanup = () => {
            for (const name of propNames) newProps._clearTransitionValue(name);
        };

        const duration = fallbackTransition.duration + (fallbackTransition.delay ?? 0);
        if (duration <= 0) {
            // Instant — clear overlays immediately.
            this._pagePropertyCleanup();
            this._pagePropertyCleanup = null;
            return 0;
        }

        const tm = getTransitionManager(this.sceneObject.babylonScene);
        const key = `pageProps:${this.sceneObject.transform.uniqueId}`;

        tm.start(key, fallbackTransition, (progress) => {
            for (const [name, { from, to }] of candidates) {
                // === 1: completion only — overshooting easings exceed 1 mid-flight
                if (progress === 1) {
                    newProps._clearTransitionValue(name);
                } else {
                    newProps._setTransitionValue(name, interpolateValue(from, to, progress));
                }
            }
        }, () => {
            // On complete: clear all overlays and cleanup registration.
            for (const name of propNames) {
                newProps._clearTransitionValue(name);
            }
            this._pagePropertyCleanup = null;
        });

        return duration;
    }

    /**
     * Whether this page's `active` is a property reference (`$name`) rather
     * than a plain boolean. Read from the AUTHORED data, not the resolved
     * flag: the resolved value is a boolean either way, and it is the authored
     * form this decides on. `getSceneObjectData()` hands back a deep clone, so
     * this is called only when a correction is otherwise about to happen.
     */
    private _hasBoundActive(node: TransformNode): boolean {
        const obj = this.sceneObject.traceScene.findSceneObjectByTransform(node);
        return typeof obj?.getSceneObjectData().active === 'string';
    }

    /**
     * Activate or deactivate a single page node.
     * If the node belongs to a SceneObject, uses setActive() to keep
     * serialized state in sync. Otherwise falls back to setEnabled().
     */
    private _setPageActive(node: TransformNode, active: boolean): void {
        const obj = this.sceneObject.traceScene.findSceneObjectByTransform(node);
        if (obj) {
            obj.setActive(active);
        } else {
            node.setEnabled(active);
        }
    }

    /**
     * Start the autoplay timer. Only activates via play() (which sets _playing)
     * or when the legacy autoplay data field is set. Not triggered on attach —
     * autoplay requires play mode or an explicit play() call.
     */
    private _startAutoplay(): void {
        if (!this._resolved.autoplay && !this._playing) return;

        // Idempotent: stop any existing timer before starting a new one
        this._stopAutoplay();

        const duration = (this._resolved.pageDuration ?? 5) * 1000;
        Logger.debug(`[Pages] Starting autoplay on ${this.sceneObject.name} (${duration}ms per page, loop=${this._resolved.loop ?? false})`);
        this._scheduleNext(duration);
    }

    private _stopAutoplay(): void {
        if (this._autoplayTimer !== null) {
            this._autoplayTimer();
            this._autoplayTimer = null;
            Logger.debug(`[Pages] Stopped autoplay on ${this.sceneObject.name}`);
        }
    }

    private _scheduleNext(duration: number): void {
        this._autoplayTimer = getTransitionManager(this.scene).after(duration, () => this._autoplayTick(duration));
    }

    private _autoplayTick(_duration: number): void {
        this._autoplayTimer = null;

        // A pending sequenced swap owns the deck's next destination: the tick
        // fast-forwards it (same-target rule in showPage) instead of computing
        // a fresh target from the not-yet-flipped active index — which would
        // hijack an in-flight user navigation. The dwell then re-anchors via
        // showPage as usual. This is also the wedge-recovery path: a cascade
        // whose clip never settles is fast-forwarded at the next tick.
        if (this._pendingSwap !== null) {
            this.showPage(this._pendingSwap.targetIndex, true);
            return;
        }

        const nodes = this.getPageNodes();
        if (nodes.length < 2) return;

        let newIndex = this._activeIndex + 1;

        if (newIndex >= nodes.length) {
            if (this._resolved.loop) {
                newIndex = 0;
            } else {
                Logger.debug(`[Pages] Autoplay finished on ${this.sceneObject.name} (last page, no loop)`);
                this._playing = false;
                return;
            }
        }

        if (newIndex === this._activeIndex) return;

        // Delegate to showPage which resets the autoplay timer
        this.showPage(newIndex, true);
    }
}
